import { Routes, Route, Navigate } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import MainLayout from '../../../layouts/MainLayout';
import { ProtectedRoute } from '@/components/ccms-components/auth/components/ProtectedRoute';
import { Loader, Center } from '@/components/ui';

// Eager-load the dashboard for instant access on the landing route.
import ProjectsDashboard from './dashboard';

// Lazy-load the remaining pages so each chunk is only fetched on demand.
const ProjectListPage = lazy(() => import('./list'));
const ProjectBoardPage = lazy(() => import('./board'));
const ProjectCalendarPage = lazy(() => import('./calendar'));
const MyProjectsPage = lazy(() => import('./my-projects'));
const ProjectReportsPage = lazy(() => import('./reports'));
const PortfoliosPage = lazy(() => import('./portfolios'));
const CreateProjectPage = lazy(() => import('./create'));
const ProjectDetailPage = lazy(() => import('./view/[id]'));
const EditProjectPage = lazy(() => import('./edit/[id]'));

// Admin pages (role-gated)
const AdminIndex = lazy(() => import('./admin'));
const AdminDashboard = lazy(() => import('./admin/dashboard'));
const AdminLookupsIndex = lazy(() => import('./admin/lookups'));
const LookupCategoryPage = lazy(() => import('./admin/lookups/[category]'));
const LookupManagementPage = lazy(() => import('./admin/lookups/[category]/[lookup]'));
const AdminSettings = lazy(() => import('./admin/settings'));

/** Same any-of roles the Projects menu uses for its admin items. */
const PROJECTS_ADMIN_ROLES = ['Admin', 'ProjectAdmin'];

const LoadingFallback = () => (
  <Center h={400}>
    <Loader size="lg" />
  </Center>
);

/**
 * Project Management Routes
 *
 * Complete routing configuration for the Projects domain, wrapped in the
 * shared MainLayout (sidebar, topbar, breadcrumbs) scoped to the
 * "projects" domain key. Paths mirror the folder structure and the
 * `PROJECTS_ROUTES` constants in `@/config/routes`.
 *
 *   /                              -> Overview dashboard
 *   /list                          -> Project list/grid
 *   /board                         -> Kanban board
 *   /calendar                      -> Calendar / timeline view
 *   /my-projects                   -> Projects I own / am on
 *   /reports                       -> Reports & analytics
 *   /portfolios                    -> Portfolios (Admin)
 *   /create                        -> New project wizard
 *   /view/:id                      -> Project detail
 *   /edit/:id                      -> Edit project
 *   /admin                         -> Admin landing (Admin)
 *   /admin/dashboard               -> Admin dashboard (Admin)
 *   /admin/lookups                 -> Lookup categories (Admin)
 *   /admin/lookups/:category       -> Lookups within a category (Admin)
 *   /admin/lookups/:category/:lookup -> Manage a single lookup (Admin)
 *   /admin/settings                -> Domain settings (Admin)
 */
export default function ProjectManagementRoutes() {
  return (
    <MainLayout domainKey="projects">
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          {/* Overview — default landing route */}
          <Route path="/" element={<ProjectsDashboard />} />

          {/* User-facing pages */}
          <Route path="/list" element={<ProjectListPage />} />
          <Route path="/board" element={<ProjectBoardPage />} />
          <Route path="/calendar" element={<ProjectCalendarPage />} />
          <Route path="/my-projects" element={<MyProjectsPage />} />
          <Route path="/reports" element={<ProjectReportsPage />} />
          <Route path="/create" element={<CreateProjectPage />} />
          <Route path="/view/:id" element={<ProjectDetailPage />} />
          <Route path="/edit/:id" element={<EditProjectPage />} />

          <Route
            path="/portfolios"
            element={
              <ProtectedRoute requiredRoles={PROJECTS_ADMIN_ROLES}>
                <PortfoliosPage />
              </ProtectedRoute>
            }
          />

          {/* Admin section — role-gated */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute requiredRoles={PROJECTS_ADMIN_ROLES}>
                <AdminIndex />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute requiredRoles={PROJECTS_ADMIN_ROLES}>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lookups"
            element={
              <ProtectedRoute requiredRoles={PROJECTS_ADMIN_ROLES}>
                <AdminLookupsIndex />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lookups/:category"
            element={
              <ProtectedRoute requiredRoles={PROJECTS_ADMIN_ROLES}>
                <LookupCategoryPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/lookups/:category/:lookup"
            element={
              <ProtectedRoute requiredRoles={PROJECTS_ADMIN_ROLES}>
                <LookupManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute requiredRoles={PROJECTS_ADMIN_ROLES}>
                <AdminSettings />
              </ProtectedRoute>
            }
          />

          {/* Fallback — unknown project routes return to the overview */}
          <Route path="*" element={<Navigate to="/app/projects" replace />} />
        </Routes>
      </Suspense>
    </MainLayout>
  );
}
